import React, { useCallback, useEffect, useState } from "react";
import { DefaultButton, PrimaryButton, TextField } from "@fluentui/react";
import CustomBreadCrumb from "../../components/CustomBreadCrumb";
import { downloadAiDraftModelStatsCsv, getAiDraftModelStats } from "../../service/AiDraftService";

export const breadCrumbItems = [{ text: "AI draft models", key: "ai-draft-models" }];

const percent = (value) => (value == null ? "–" : `${Math.round(value * 1000) / 10}%`);
const seconds = (ms) => (ms == null ? "–" : `${(ms / 1000).toFixed(1)} s`);

/**
 * Super-admin comparison of the AI draft models: drafts produced, failures, latency and how often
 * radiologists inserted each model's draft when several were offered. Real model ids are shown
 * here even when the viewer uses blind labels.
 */
const AiDraftModelStats = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const response = await getAiDraftModelStats(from, to);
    setLoading(false);
    if (response?.status === 200) {
      setRows(response.data?.data?.models || []);
      setRange({ from: response.data?.data?.from, to: response.data?.data?.to });
    } else {
      setError(response?.data?.message || "Could not load the statistics");
    }
  }, [from, to]);

  useEffect(() => {
    load();
    // first load only; later loads are explicit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async () => {
    const response = await downloadAiDraftModelStatsCsv(from, to);
    if (response?.status !== 200) {
      setError("Could not download the CSV");
      return;
    }
    const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `ai-draft-model-stats-${range?.from || "from"}-to-${range?.to || "to"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ms-Grid" dir="ltr" style={{ padding: "12px 20px" }} data-testid="ai-draft-model-stats">
      <CustomBreadCrumb items={breadCrumbItems} />
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, margin: "12px 0" }}>
        <TextField label="From" type="date" value={from} onChange={(_, v) => setFrom(v || "")} />
        <TextField label="To" type="date" value={to} onChange={(_, v) => setTo(v || "")} />
        <PrimaryButton text={loading ? "Loading…" : "Show"} onClick={load} disabled={loading} />
        <DefaultButton text="Download CSV" onClick={download} />
      </div>
      {range && (
        <p style={{ fontSize: 12, opacity: 0.75 }}>
          Drafts created {range.from} to {range.to}. "Selected" counts jobs where the radiologist inserted that
          model's draft; the rate is out of jobs where a draft was inserted.
        </p>
      )}
      {error && (
        <p role="alert" style={{ color: "#e5484d" }}>
          {error}
        </p>
      )}
      <table className="ai-draft-stats-table" style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
        <thead>
          <tr>
            {["Model", "Drafts", "Failures", "Avg time", "Median time", "90th pct time", "Selected", "Selection rate", "Avg tokens in / out"].map((h) => (
              <th key={h} style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid rgba(127,127,127,.4)" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !loading && (
            <tr>
              <td colSpan={9} style={{ padding: 8, opacity: 0.7 }}>
                No drafts in this period.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.model_id}>
              <td style={{ padding: "6px 8px" }}>
                <div style={{ fontWeight: 600 }}>{r.label}</div>
                {r.label !== r.model_id && <div style={{ fontSize: 11, opacity: 0.7 }}>{r.model_id}</div>}
              </td>
              <td style={{ padding: "6px 8px" }}>{r.drafts}</td>
              <td style={{ padding: "6px 8px" }}>
                {r.failures} ({percent(r.failure_rate)})
              </td>
              <td style={{ padding: "6px 8px" }}>{seconds(r.avg_latency_ms)}</td>
              <td style={{ padding: "6px 8px" }}>{seconds(r.median_latency_ms)}</td>
              <td style={{ padding: "6px 8px" }}>{seconds(r.p90_latency_ms)}</td>
              <td style={{ padding: "6px 8px" }}>
                {r.selected} / {r.jobs_with_selection}
              </td>
              <td style={{ padding: "6px 8px" }}>{percent(r.selection_rate)}</td>
              <td style={{ padding: "6px 8px" }}>
                {r.avg_input_tokens ?? "–"} / {r.avg_output_tokens ?? "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AiDraftModelStats;
